import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { getShareInfo, getShareDownloadUrl, ShareInfo } from "@/services/mediaShareService";
import { useSharePolling, PollResult, SLOW_POLL_INTERVAL_MS } from "@/domains/LibraryView/components/hooks/useSharePolling";
import { formatFileSize, formatExpiryRelative } from "@/domains/LibraryView/components/utils/shareFormatters";

type PageState = "loading" | "creating" | "ready" | "expired" | "failed" | "error";

export const ShareDownloadPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<PageState>("loading");
  const [shareInfo, setShareInfo] = useState<ShareInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handlePollResult = useCallback((result: PollResult) => {
    switch (result.type) {
      case "ready":
        setShareInfo(result.info);
        setState("ready");
        break;
      case "failed":
        setError(result.error);
        setState("failed");
        break;
      case "creating":
        setShareInfo(result.info);
        setState("creating");
        break;
      case "expired":
        setState("expired");
        break;
      case "timeout":
        setError("Still preparing. Please refresh the page or check back in a few minutes.");
        setState("error");
        break;
      case "error":
        setError(result.error);
        setState("error");
        break;
    }
  }, []);

  const { startPolling, stopPolling } = useSharePolling({
    onResult: handlePollResult,
    intervalMs: SLOW_POLL_INTERVAL_MS,
  });

  // Initial fetch on mount
  useEffect(() => {
    if (!token) {
      setState("error");
      setError("Invalid share link");
      return;
    }

    // Do an initial fetch, then start polling if needed
    const fetchInitial = async () => {
      try {
        const info = await getShareInfo(token);
        setShareInfo(info);

        if (info.status === "ready") {
          setState("ready");
        } else if (info.status === "failed") {
          setError(info.error || "Failed to create download package");
          setState("failed");
        } else if (info.status === "creating") {
          setState("creating");
          startPolling(token);
        } else if (info.status === "expired" || info.status === "deleted") {
          setState("expired");
        }
      } catch (err: unknown) {
        const axiosError = err as { response?: { status?: number }; message?: string };
        if (axiosError.response?.status === 404) {
          setState("expired");
        } else {
          setState("error");
          setError(axiosError.message || "Failed to load share info");
        }
      }
    };

    fetchInitial();

    return () => {
      stopPolling();
    };
  }, [token, startPolling, stopPolling]);

  const handleDownload = () => {
    if (token) {
      window.location.href = getShareDownloadUrl(token);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Loading state */}
        {state === "loading" && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading download info...</p>
          </div>
        )}

        {/* Creating state - ZIP being processed */}
        {state === "creating" && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Preparing Your Download</h1>
            <p className="text-gray-600">
              Your files are being packaged. This typically takes a few minutes.
            </p>
            {shareInfo && (
              <p className="text-gray-500 text-sm mt-2">
                {shareInfo.fileCount} file{shareInfo.fileCount !== 1 ? "s" : ""} in package
              </p>
            )}
            <p className="text-gray-400 text-sm mt-4">
              This page will automatically refresh when ready.
            </p>
          </div>
        )}

        {/* Expired state */}
        {state === "expired" && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-amber-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Link Expired</h1>
            <p className="text-gray-600">
              This download link has expired or is no longer available.
            </p>
            <p className="text-gray-500 text-sm mt-4">
              Please contact the person who shared this link with you.
            </p>
          </div>
        )}

        {/* Failed state - processing error */}
        {state === "failed" && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Processing Failed</h1>
            <p className="text-gray-600">
              {error || "Failed to create the download package."}
            </p>
            <p className="text-gray-500 text-sm mt-4">
              Please contact the person who shared this link with you.
            </p>
          </div>
        )}

        {/* Error state */}
        {state === "error" && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h1>
            <p className="text-gray-600">{error || "Failed to load download info"}</p>
          </div>
        )}

        {/* Ready state */}
        {state === "ready" && shareInfo && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            {/* Header */}
            <div className="bg-linear-to-r from-blue-600 to-blue-700 px-8 py-6 text-white">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <svg
                    className="w-7 h-7"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                    />
                  </svg>
                </div>
                <div>
                  <h1 className="text-xl font-semibold">Media Download</h1>
                  <p className="text-blue-100 text-sm mt-0.5">
                    {shareInfo.fileCount} file{shareInfo.fileCount !== 1 ? "s" : ""} ready
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-8">
              {/* File info */}
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-600">Files</span>
                  <span className="font-medium text-gray-900">
                    {shareInfo.fileCount} file{shareInfo.fileCount !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-600">Total size</span>
                  <span className="font-medium text-gray-900">
                    {formatFileSize(shareInfo.totalSizeBytes)}
                  </span>
                </div>
                {shareInfo.downloadCount > 0 && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">Downloads</span>
                    <span className="font-medium text-gray-900">{shareInfo.downloadCount}</span>
                  </div>
                )}
              </div>

              {/* Download button */}
              <button
                onClick={handleDownload}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                Download ZIP
              </button>

              {/* Expiry notice */}
              {shareInfo.expiresAt && (
                <p className="text-center text-gray-500 text-sm mt-4">
                  {formatExpiryRelative(shareInfo.expiresAt)}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-gray-400 text-xs mt-6">BeNerdy Media Sharing</p>
      </div>
    </div>
  );
};
